import { useMemo } from "react";
import { useBuilderStore } from "@circle-react-shared/BuilderUI";
import type { StyleValue } from "@circle-react-shared/BuilderUI/StyleValue.types";
import { parseStyles } from "@circle-react-shared/BuilderUI/utils/parseStyles";
import {
  getVisualEmailBuilderStyleMap,
  resolveVariableValuesFromStyleObject,
} from "./getVisualEmailBuilderStyleMap";

export const useParseStyles = ({
  baseStyles = {},
  styles,
  defaultStyles,
}: {
  /** To be applied on the element directly. These properties are not editable on the design panel. */
  baseStyles?: Record<string, unknown>;
  /** Applied via the design panel. */
  styles: Record<string, StyleValue>;
  /** Default styles for the block. parseStyles iterates on styles, so only values in styles are considered. Other values are ignored. */
  defaultStyles: Record<string, unknown>;
}) => {
  const currentCanvasTheme = useBuilderStore(state => state.currentCanvasTheme);

  return useMemo(() => {
    // Create StyleValue objects for all defaultStyles properties
    // This ensures properties like fontFamily that aren't in styles are still processed
    const mergedStyles: Record<string, StyleValue> = {};

    // First, add all properties from defaultStyles as StyleValue objects
    Object.keys(defaultStyles).forEach(key => {
      if (defaultStyles[key] !== undefined && defaultStyles[key] !== null) {
        mergedStyles[key] = {
          type: "text",
          value: defaultStyles[key],
        };
      }
    });

    // Then override with actual styles from the design panel
    Object.assign(mergedStyles, styles);

    const inlineStyles = parseStyles({
      styles: mergedStyles,
      defaultStyles,
      ignoreDefaults: false,
      styleMap: getVisualEmailBuilderStyleMap(currentCanvasTheme),
    });

    const getSizingStyles = () => {
      if (!styles.sizing || !styles.sizing.value || !defaultStyles.sizing)
        return {};

      return {
        ...defaultStyles.sizing,
        ...styles.sizing.value,
      };
    };

    // Merge all styles together
    const elementStyles = {
      ...baseStyles,
      ...inlineStyles,
      ...getSizingStyles(),
    };

    return resolveVariableValuesFromStyleObject({
      styleObject: elementStyles,
      currentTheme: currentCanvasTheme,
    });
  }, [currentCanvasTheme, styles, defaultStyles, baseStyles]);
};
